import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Check, Mail, PartyPopper } from "lucide-react";
import { STORE } from "../data/site";
import { AppleMark, PlayStoreMark } from "../components/landing/BrandMarks";
import { Footer } from "../components/landing/Footer";

const ease = [0.22, 1, 0.36, 1];

const STEPS = [
  {
    title: "Download the app",
    body: "Grab Radiate from the App Store or Google Play using the buttons above.",
  },
  {
    title: "Open Radiate & tap Sign in",
    body: "On the welcome screen, choose “Sign in” rather than creating a new account.",
  },
  {
    title: "Use your purchase email",
    body: "Sign in with the exact same email you used to buy your membership — your access unlocks automatically, no codes needed.",
  },
  {
    title: "Begin your first practice",
    body: "Head to “For you”, press play, and let your first session meet you where you are.",
  },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease, delay: 0.15 + i * 0.08 },
  }),
};

export default function DownloadWelcome() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="grain relative min-h-screen overflow-hidden bg-[#0a0a0c]" data-testid="download-welcome">
      {/* glows */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="orb orb-violet absolute -top-32 left-1/4 h-[34rem] w-[34rem] rounded-full bg-[#7c3aed] blur-[150px] opacity-30" />
        <div className="orb orb-peach absolute top-10 right-0 h-[30rem] w-[30rem] rounded-full bg-[#ffb7b2] blur-[150px] opacity-25" />
        <div className="orb orb-indigo absolute bottom-0 left-0 h-[28rem] w-[28rem] rounded-full bg-[#4f46e5] blur-[160px] opacity-20" />
      </div>

      <div className="relative z-10 mx-auto max-w-3xl px-6 pb-24 pt-24 md:pt-32">
        <motion.div variants={fade} initial="hidden" animate="show" className="flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.3em] text-white/70">
            <Sparkles className="h-3.5 w-3.5 text-[#ffb7b2]" /> Welcome to Radiate
          </span>
        </motion.div>

        <motion.h1
          variants={fade}
          custom={1}
          initial="hidden"
          animate="show"
          className="mt-8 text-center font-display text-5xl font-thin leading-[1.05] tracking-tight text-white md:text-7xl"
        >
          You're in.
          <br />
          <span className="italic text-white/85">Welcome to the community.</span>
        </motion.h1>

        <motion.p
          variants={fade}
          custom={2}
          initial="hidden"
          animate="show"
          className="mx-auto mt-7 max-w-xl text-center text-base leading-relaxed text-white/60 md:text-lg"
        >
          Thank you for joining us. You've just made a powerful choice to step
          into your next level and we're so glad you're here. Let's get the app
          onto your phone in a few steps.
        </motion.p>

        {/* store buttons */}
        <motion.div
          variants={fade}
          custom={3}
          initial="hidden"
          animate="show"
          className="mt-11 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <a
            href={STORE.appStore}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 rounded-2xl border border-white/15 bg-white px-7 py-3.5 text-black transition-transform duration-300 hover:scale-[1.04] hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]"
            data-testid="welcome-app-store"
          >
            <AppleMark className="h-7 w-7 text-black" />
            <span className="text-left leading-tight">
              <span className="block text-[10px] uppercase tracking-wider text-black/60">Download on the</span>
              <span className="block font-display text-lg font-medium">App Store</span>
            </span>
          </a>
          <a
            href={STORE.googlePlay}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 rounded-2xl border border-white/15 bg-white px-7 py-3.5 text-black transition-transform duration-300 hover:scale-[1.04] hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]"
            data-testid="welcome-google-play"
          >
            <PlayStoreMark className="h-7 w-7" />
            <span className="text-left leading-tight">
              <span className="block text-[10px] uppercase tracking-wider text-black/60">Get it on</span>
              <span className="block font-display text-lg font-medium">Google Play</span>
            </span>
          </a>
        </motion.div>

        {/* email note */}
        <motion.div
          variants={fade}
          custom={4}
          initial="hidden"
          animate="show"
          className="mx-auto mt-10 flex max-w-xl items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl"
          data-testid="welcome-email-note"
        >
          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/5 text-[#ffb7b2]">
            <Mail className="h-4 w-4" />
          </span>
          <p className="text-sm leading-relaxed text-white/65">
            <span className="font-semibold text-white">Important:</span> sign in
            with the <span className="text-white">same email you used to purchase</span>.
            That's what connects your membership — your full access appears the
            moment you log in.
          </p>
        </motion.div>

        {/* steps */}
        <div className="mt-16">
          <motion.h2
            variants={fade}
            custom={5}
            initial="hidden"
            animate="show"
            className="mb-8 text-center text-xs font-semibold uppercase tracking-[0.35em] text-white/40"
          >
            How to get started
          </motion.h2>

          <div className="space-y-4">
            {STEPS.map((s, i) => (
              <motion.div
                key={i}
                variants={fade}
                custom={6 + i}
                initial="hidden"
                animate="show"
                className="flex items-start gap-5 rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition-colors hover:bg-white/[0.04]"
                data-testid={`welcome-step-${i + 1}`}
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/15 font-display text-sm text-white/80">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-display text-lg font-normal text-white">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/55">{s.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* closing */}
        <motion.div
          variants={fade}
          custom={11}
          initial="hidden"
          animate="show"
          className="mt-16 rounded-[2rem] border border-white/10 bg-white/[0.03] p-8 text-center backdrop-blur-xl md:p-12"
        >
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full border border-white/15 bg-white/5 text-[#ffb7b2]">
            <PartyPopper className="h-5 w-5" />
          </div>
          <p className="mt-5 font-display text-2xl font-thin tracking-tight text-white md:text-3xl">
            Here's to coming home to yourself.
          </p>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/55">
            You now have every guided journey, breathwork session and daily
            prompt at your fingertips. We can't wait to grow alongside you.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/40">
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-[#ffb7b2]" /> Membership active
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-[#ffb7b2]" /> New sessions weekly
            </span>
            <a href="mailto:info@radiateapp.com" className="underline underline-offset-4 hover:text-white">
              Need help? info@radiateapp.com
            </a>
          </div>
          <Link
            to="/"
            className="mt-8 inline-block text-xs uppercase tracking-[0.2em] text-white/40 transition-colors hover:text-white"
            data-testid="welcome-home-link"
          >
            Back to radiateapp.com
          </Link>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
}
